import { S3Client, HeadBucketCommand, ListObjectsV2Command, DeleteObjectCommand, GetObjectCommand } from '@aws-sdk/client-s3';
import { Upload } from '@aws-sdk/lib-storage';
import { getSignedUrl } from '@aws-sdk/s3-request-presigner';

/**
 * S3 Upload Service with REAL progress tracking and error handling
 * 
 * Features:
 * - Real progress tracking via AWS SDK Upload class
 * - Multipart upload for large files (> 5MB)
 * - Detailed console logging for debugging
 * - Connection testing
 * - Cancellation support
 */

class S3UploadService {
  constructor() {
    this.s3Client = null;
    this.bucketConfig = null;
    this.debugMode = true;
    this.activeUploads = new Map();
    this.uploadTimeouts = new Map();
    this.activeFolder = null; // Store active folder path
  }

  /**
   * Log debug information to console
   */
  log(message, data = null) {
    if (this.debugMode) {
      console.log(`[S3UploadService] ${message}`, data || '');
    }
  }

  /**
   * Log error information to console
   */
  logError(message, error) {
    console.error(`[S3UploadService ERROR] ${message}`, {
      error: error?.message || error,
      stack: error?.stack,
      name: error?.name,
      code: error?.$metadata?.httpStatusCode || error?.code
    });
  }

  /**
   * Initialize S3 client with bucket configuration
   */
  async initializeClient(bucketConfig) {
    try {
      this.log('Initializing S3 client...', {
        bucketName: bucketConfig?.bucketName,
        region: bucketConfig?.bucketRegion,
        hasAccessKey: !!bucketConfig?.accessKeyId,
        hasSecretKey: !!bucketConfig?.secretAccessKey
      });

      if (!bucketConfig?.accessKeyId || !bucketConfig?.secretAccessKey) {
        throw new Error('Missing AWS credentials (Access Key ID or Secret Access Key)');
      }

      if (!bucketConfig?.bucketRegion) {
        throw new Error('Missing AWS region');
      }

      this.bucketConfig = bucketConfig;

      this.s3Client = new S3Client({
        region: bucketConfig?.bucketRegion,
        credentials: {
          accessKeyId: bucketConfig?.accessKeyId,
          secretAccessKey: bucketConfig?.secretAccessKey
        },
        // Browser-specific configuration
        forcePathStyle: false,
        apiVersion: '2006-03-01',
        // Request configuration
        requestHandler: {
          connectionTimeout: 60000, // 60 seconds connection timeout
          requestTimeout: 600000 // 10 minutes request timeout
        }
      });

      this.log('S3 client initialized successfully');
      
      return { success: true };
    } catch (error) {
      this.logError('Failed to initialize S3 client', error);
      throw new Error(`S3 initialization failed: ${error?.message}`);
    }
  }

  /**
   * Test connection to S3 bucket
   */
  async testConnection(bucketConfig) {
    try {
      this.log('Testing S3 connection...');
      
      await this.initializeClient(bucketConfig);

      const command = new HeadBucketCommand({
        Bucket: bucketConfig?.bucketName
      });

      this.log('Sending HeadBucket command...');
      let response = await this.s3Client?.send(command);
      
      this.log('Connection test successful', {
        statusCode: response?.$metadata?.httpStatusCode,
        requestId: response?.$metadata?.requestId
      });

      return { 
        success: true, 
        message: 'Successfully connected to S3 bucket',
        details: {
          bucketName: bucketConfig?.bucketName,
          region: bucketConfig?.bucketRegion,
          statusCode: response?.$metadata?.httpStatusCode
        }
      };
    } catch (error) {
      this.logError('Connection test failed', error);
      
      // Provide specific error messages based on error type
      let errorMessage = 'Failed to connect to S3 bucket';
      
      if (error?.name === 'NoSuchBucket') {
        errorMessage = `Bucket '${bucketConfig?.bucketName}' does not exist`;
      } else if (error?.name === 'InvalidAccessKeyId') {
        errorMessage = 'Invalid Access Key ID';
      } else if (error?.name === 'SignatureDoesNotMatch') {
        errorMessage = 'Invalid Secret Access Key';
      } else if (error?.name === 'AccessDenied' || error?.name === 'Forbidden') {
        errorMessage = 'Access denied - check bucket permissions';
      } else if (error?.name === 'NetworkingError') {
        errorMessage = 'Network error - check your internet connection';
      }

      return { 
        success: false, 
        error: errorMessage,
        details: {
          errorName: error?.name,
          errorCode: error?.$metadata?.httpStatusCode || error?.code,
          errorMessage: error?.message
        }
      };
    }
  }

  /**
   * Create an abort controller for canceling uploads
   */
  createUploadAbortController(uploadId) {
    const abortController = new AbortController();
    this.activeUploads?.set(uploadId, abortController);
    return abortController;
  }

  /**
   * Cancel an active upload
   */
  cancelUpload(uploadId) {
    const abortController = this.activeUploads?.get(uploadId);
    if (abortController) {
      this.log(`🛑 Canceling upload: ${uploadId}`);
      abortController?.abort();
      this.activeUploads?.delete(uploadId);
      
      // Clear associated timeout
      const timeoutId = this.uploadTimeouts?.get(uploadId);
      if (timeoutId) {
        clearTimeout(timeoutId);
        this.uploadTimeouts?.delete(uploadId);
      }
      
      return true;
    }
    return false;
  }

  /**
   * Set active folder for scoped operations
   * @param {string} folderPath - Active folder path (e.g., "username/storage")
   */
  setActiveFolder(folderPath) {
    this.activeFolder = folderPath;
    this.log('Active folder set', { folderPath });
  }

  /**
   * Get active folder path
   * @returns {string|null} Active folder path or null
   */
  getActiveFolder() {
    return this.activeFolder;
  }

  /**
   * Clear active folder
   */
  clearActiveFolder() {
    this.activeFolder = null;
    this.log('Active folder cleared');
  }

  /**
   * Create a folder in S3 by uploading a placeholder object
   * S3 doesn't have true folders, so we create a 0-byte placeholder file
   * @param {string} folderPath - Folder path to create (e.g., "username/storage/")
   * @returns {Promise<Object>} - Result of folder creation
   */
  async createFolder(folderPath) {
    try {
      // Ensure folder path ends with '/'
      const normalizedPath = folderPath?.endsWith('/') ? folderPath : `${folderPath}/`;
      
      this.log('Creating folder in S3...', {
        folderPath: normalizedPath,
        bucket: this.bucketConfig?.bucketName
      });

      if (!this.s3Client || !this.bucketConfig) {
        throw new Error('S3 client not initialized. Call initializeClient first.');
      }

      // Create a 0-byte placeholder file to represent the folder
      // This is the standard way to create folders in S3
      const placeholderKey = `${normalizedPath}.placeholder`;
      
      const upload = new Upload({
        client: this.s3Client,
        params: {
          Bucket: this.bucketConfig?.bucketName,
          Key: placeholderKey,
          Body: new Uint8Array(0), // Empty content
          ContentType: 'application/x-directory',
          Metadata: {
            type: 'folder_placeholder',
            createdAt: new Date()?.toISOString()
          }
        }
      });

      const result = await upload?.done();

      this.log('Folder created successfully in S3', {
        folderPath: normalizedPath,
        placeholderKey: placeholderKey,
        etag: result?.ETag
      });

      return {
        success: true,
        folderPath: normalizedPath,
        key: placeholderKey,
        message: 'Folder created successfully in S3'
      };

    } catch (error) {
      this.logError('Failed to create folder in S3', error);

      // Provide specific error messages
      let errorMessage = 'Failed to create folder';
      
      if (error?.name === 'NoSuchBucket') {
        errorMessage = 'Bucket does not exist';
      } else if (error?.name === 'AccessDenied') {
        errorMessage = 'Access denied - check bucket permissions';
      }

      throw new Error(`${errorMessage}: ${error?.message}`);
    }
  }

  /**
   * Delete a folder from S3 (recursively deletes all objects with the folder prefix)
   * @param {string} folderPath - Folder path to delete (e.g., "username/storage/")
   * @param {Function} progressCallback - Optional callback for tracking deletion progress
   * @returns {Promise<Object>} - Result of folder deletion
   */
  async deleteFolder(folderPath, progressCallback = null) {
    try {
      // Ensure folder path ends with '/'
      const normalizedPath = folderPath?.endsWith('/') ? folderPath : `${folderPath}/`;
      
      this.log('Deleting folder from S3...', {
        folderPath: normalizedPath,
        bucket: this.bucketConfig?.bucketName
      });

      if (!this.s3Client || !this.bucketConfig) {
        throw new Error('S3 client not initialized. Call initializeClient first.');
      }

      // First, list all objects in the folder
      const listResult = await this.listFiles({ prefix: normalizedPath, maxKeys: 1000 });

      if (listResult?.files?.length === 0) {
        this.log('Folder is empty or does not exist, nothing to delete', {
          folderPath: normalizedPath
        });
        return {
          success: true,
          folderPath: normalizedPath,
          deletedCount: 0,
          message: 'Folder was empty or did not exist'
        };
      }

      // Extract keys from files
      const keysToDelete = listResult?.files?.map(file => file?.path);

      this.log('Deleting folder contents...', {
        folderPath: normalizedPath,
        fileCount: keysToDelete?.length
      });

      // Delete all files in the folder
      const deleteResult = await this.deleteMultipleFiles(keysToDelete, progressCallback);

      this.log('Folder deleted successfully from S3', {
        folderPath: normalizedPath,
        deletedCount: deleteResult?.successful?.length,
        failedCount: deleteResult?.failed?.length
      });

      return {
        success: true,
        folderPath: normalizedPath,
        deletedCount: deleteResult?.successful?.length,
        failedCount: deleteResult?.failed?.length,
        message: `Folder deleted: ${deleteResult?.successful?.length} files removed`
      };

    } catch (error) {
      this.logError('Failed to delete folder from S3', error);
      throw new Error(`Failed to delete folder: ${error?.message}`);
    }
  }

  /**
   * Check if a folder exists in S3
   * @param {string} folderPath - Folder path to check (e.g., "username/storage/")
   * @returns {Promise<boolean>} - True if folder exists, false otherwise
   */
  async folderExists(folderPath) {
    try {
      const normalizedPath = folderPath?.endsWith('/') ? folderPath : `${folderPath}/`;
      
      this.log('Checking if folder exists in S3...', {
        folderPath: normalizedPath
      });

      if (!this.s3Client || !this.bucketConfig) {
        throw new Error('S3 client not initialized. Call initializeClient first.');
      }

      // List objects with the folder prefix, limit to 1 to check existence
      const command = new ListObjectsV2Command({
        Bucket: this.bucketConfig?.bucketName,
        Prefix: normalizedPath,
        MaxKeys: 1
      });

      let response = await this.s3Client?.send(command);
      const exists = response?.KeyCount > 0;

      this.log('Folder existence check complete', {
        folderPath: normalizedPath,
        exists: exists
      });

      return exists;

    } catch (error) {
      this.logError('Failed to check folder existence', error);
      return false;
    }
  }

  /**
   * Upload file to S3 with REAL progress tracking using AWS SDK Upload class
   * Now supports folder scoping and PROPER checksum handling for Object Lock buckets
   */
  async uploadFile(file, options = {}) {
    const uploadId = options?.uploadId || `upload-${Date.now()}-${Math.random()?.toString(36)?.substr(2, 9)}`;
    const uploadTimeout = options?.timeout || 600000; // Default 10 minute timeout
    
    try {
      this.log('🚀 Starting file upload with REAL progress tracking...', {
        uploadId,
        fileName: file?.name,
        fileSize: `${(file?.size / 1024 / 1024)?.toFixed(2)} MB`,
        fileType: file?.type,
        destinationFolder: options?.destinationFolder,
        activeFolder: this.activeFolder,
        timeout: `${uploadTimeout / 1000}s`
      });

      if (!this.s3Client || !this.bucketConfig) {
        throw new Error('S3 client not initialized. Call initializeClient first.');
      }

      // Construct S3 key with active folder scoping
      let folder = '';
      
      // If active folder is set, use it as base
      if (this.activeFolder) {
        folder = `${this.activeFolder}/`;
      } else if (options?.destinationFolder && options?.destinationFolder !== 'root') {
        folder = `${options?.destinationFolder}/`;
      }
      
      const key = `${folder}${file?.name}`;

      this.log('📤 Configuring multipart upload with checksum support...', {
        uploadId,
        bucket: this.bucketConfig?.bucketName,
        key: key,
        activeFolder: this.activeFolder,
        contentType: file?.type,
        partSize: '5 MB (AWS SDK default for multipart)',
        checksumAlgorithm: 'CRC32C (for Object Lock compatibility)'
      });

      // Create AbortController for cancellation
      const abortController = new AbortController();
      this.activeUploads?.set(uploadId, abortController);

      // CRITICAL FIX: Enable CRC32C checksum algorithm for Object Lock bucket compatibility
      // This ensures consistent checksums across all multipart upload operations
      const upload = new Upload({
        client: this.s3Client,
        params: {
          Bucket: this.bucketConfig?.bucketName,
          Key: key,
          Body: file,
          ContentType: file?.type,
          // Enable CRC32C checksum for Object Lock compatibility
          // AWS SDK will automatically calculate and include checksums for ALL parts
          ChecksumAlgorithm: 'CRC32C',
          Metadata: {
            originalName: file?.name,
            uploadedAt: new Date()?.toISOString(),
            ...(options?.preserveMetadata && {
              size: file?.size?.toString(),
              type: file?.type
            })
          }
        },
        // Multipart upload configuration
        queueSize: 4, // Number of concurrent part uploads
        partSize: 5 * 1024 * 1024, // 5MB parts (AWS minimum)
        leavePartsOnError: false, // Clean up failed uploads
        abortSignal: abortController?.signal
      });

      // Track upload progress and network activity
      const startTime = Date.now();
      let lastProgressTime = Date.now();
      let lastUploadedBytes = 0;
      let progressStallDetected = false;

      // Set up timeout detection
      const timeoutPromise = new Promise((_, reject) => {
        const timeoutId = setTimeout(() => {
          this.logError('⏰ Upload timeout exceeded', {
            uploadId,
            fileName: file?.name,
            timeoutMs: uploadTimeout,
            elapsed: `${((Date.now() - startTime) / 1000)?.toFixed(1)}s`
          });
          
          // Cancel the upload
          abortController?.abort();
          
          reject(new Error(`Upload timeout: File upload exceeded ${uploadTimeout / 1000} seconds. This may indicate network issues or CORS configuration problems.`));
        }, uploadTimeout);
        
        this.uploadTimeouts?.set(uploadId, timeoutId);
      });

      // Set up stall detection (no progress for 30 seconds)
      const stallCheckInterval = setInterval(() => {
        const timeSinceLastProgress = Date.now() - lastProgressTime;
        
        if (timeSinceLastProgress > 30000 && !progressStallDetected) { // 30 seconds stall
          progressStallDetected = true;
          this.log('⚠️ Upload progress stalled - no activity for 30s', {
            uploadId,
            fileName: file?.name,
            lastProgress: `${((lastUploadedBytes / file?.size) * 100)?.toFixed(1)}%`,
            stallDuration: `${(timeSinceLastProgress / 1000)?.toFixed(1)}s`
          });
          
          if (options?.onStallDetected) {
            options?.onStallDetected({
              uploadId,
              fileName: file?.name,
              stallDuration: timeSinceLastProgress
            });
          }
        }
      }, 5000); // Check every 5 seconds

      // Listen to upload progress events (REAL progress from AWS SDK)
      upload?.on('httpUploadProgress', (progress) => {
        const currentTime = Date.now();
        const uploadedBytes = progress?.loaded || 0;
        const totalBytes = progress?.total || file?.size;
        const progressPercent = Math.round((uploadedBytes / totalBytes) * 100);
        
        // Calculate real upload speed
        const elapsedSeconds = (currentTime - startTime) / 1000;
        const uploadSpeed = elapsedSeconds > 0 ? (uploadedBytes / 1024 / 1024) / elapsedSeconds : 0;
        
        // Detect if progress is actually moving
        if (uploadedBytes > lastUploadedBytes) {
          lastProgressTime = currentTime;
          lastUploadedBytes = uploadedBytes;
          progressStallDetected = false; // Reset stall detection
        }
        
        this.log('📊 Real upload progress', {
          uploadId,
          fileName: file?.name,
          progress: `${progressPercent}%`,
          uploaded: `${(uploadedBytes / 1024 / 1024)?.toFixed(2)} MB`,
          total: `${(totalBytes / 1024 / 1024)?.toFixed(2)} MB`,
          speed: `${uploadSpeed?.toFixed(2)} MB/s`,
          elapsed: `${elapsedSeconds?.toFixed(1)}s`,
          isStalled: progressStallDetected
        });
        
        // Call progress callback if provided
        if (options?.onProgress) {
          options?.onProgress({
            uploadId,
            fileName: file?.name,
            progress: progressPercent,
            uploadedBytes,
            totalBytes,
            speed: `${uploadSpeed?.toFixed(2)} MB/s`,
            elapsed: elapsedSeconds * 1000,
            isStalled: progressStallDetected
          });
        }
      });

      // Perform upload with timeout race
      this.log('🌐 Starting S3 multipart upload...', {
        uploadId,
        fileName: file?.name,
        fileSize: `${(file?.size / 1024 / 1024)?.toFixed(2)} MB`,
        checksumMode: 'CRC32C enabled (Object Lock compatible)'
      });

      const result = await Promise.race([
        upload?.done(),
        timeoutPromise
      ]);

      // Clear timeout and stall detection
      const timeoutId = this.uploadTimeouts?.get(uploadId);
      if (timeoutId) {
        clearTimeout(timeoutId);
        this.uploadTimeouts?.delete(uploadId);
      }
      clearInterval(stallCheckInterval);

      const uploadTime = Date.now() - startTime;
      const uploadSpeed = (file?.size / 1024 / 1024) / (uploadTime / 1000); // MB/s

      this.log('✅ Upload successful!', {
        uploadId,
        key: key,
        etag: result?.ETag,
        uploadTime: `${uploadTime}ms`,
        uploadSpeed: `${uploadSpeed?.toFixed(2)} MB/s`,
        location: result?.Location
      });

      // Construct file URL
      let fileUrl = this.bucketConfig?.publicUrl 
        ? `${this.bucketConfig?.publicUrl}/${key}`
        : `https://${this.bucketConfig?.bucketName}.s3.${this.bucketConfig?.bucketRegion}.amazonaws.com/${key}`;

      return {
        success: true,
        uploadId: uploadId,
        fileUrl: fileUrl,
        key: key,
        etag: result?.ETag,
        bucket: this.bucketConfig?.bucketName,
        uploadTime: uploadTime,
        uploadSpeed: uploadSpeed
      };

    } catch (error) {
      this.logError('❌ File upload failed', error);

      // Clean up
      this.activeUploads?.delete(uploadId);
      const timeoutId = this.uploadTimeouts?.get(uploadId);
      if (timeoutId) {
        clearTimeout(timeoutId);
        this.uploadTimeouts?.delete(uploadId);
      }

      // Check if it's a cancellation
      if (error?.name === 'AbortError' || error?.message?.includes('aborted')) {
        const cancelError = new Error('Upload was canceled by user');
        cancelError.name = 'UploadCanceled';
        throw cancelError;
      }

      // Check for checksum-related errors
      const isChecksumError = 
        error?.message?.includes('checksum') ||
        error?.message?.includes('crc32') ||
        error?.message?.includes('ChecksumAlgorithm') ||
        error?.code === 'InvalidRequest' && error?.message?.includes('part');

      if (isChecksumError) {
        this.logError('🔐 CHECKSUM ERROR DETECTED', {
          uploadId,
          errorCode: error?.code,
          errorMessage: error?.message,
          fileName: file?.name,
          troubleshooting: 'S3 bucket may have Object Lock or checksum requirements enabled'
        });

        const checksumError = new Error('CHECKSUM_ERROR: S3 bucket requires specific checksum validation. This may be due to Object Lock policies or bucket-level checksum requirements. Try disabling Object Lock or contact your S3 administrator.');
        checksumError.originalError = error;
        checksumError.code = 'CHECKSUM_VALIDATION_FAILED';
        throw checksumError;
      }

      // Enhanced CORS error detection
      const isCORSError = 
        error?.message?.includes('CORS') ||
        error?.message?.includes('Failed to fetch') ||
        error?.message?.includes('NetworkingError') ||
        (error?.name === 'TypeError' && error?.message === 'Failed to fetch') ||
        error?.name === 'FetchError'|| error?.message?.includes('Access-Control-Allow-Origin') ||
        error?.message?.includes('MethodNotAllowed') ||
        error?.message?.includes('preflight');

      if (isCORSError) {
        console.error('🚨 CORS ERROR DETECTED during upload!', {
          uploadId,
          errorType: error?.name,
          errorMessage: error?.message,
          bucket: this.bucketConfig?.bucketName,
          region: this.bucketConfig?.bucketRegion
        });

        const corsError = new Error('CORS_UPLOAD_ERROR');
        corsError.originalError = error;
        corsError.troubleshooting = {
          type: 'CORS_UPLOAD',
          message: 'Your S3 bucket CORS policy does not allow file uploads from the browser',
          action: 'UPDATE_CORS_POLICY',
          details: {
            bucketName: this.bucketConfig?.bucketName,
            region: this.bucketConfig?.bucketRegion,
            errorType: error?.name,
            errorMessage: error?.message,
            requiredFix: 'Add PUT and POST methods to your bucket CORS configuration'
          }
        };
        throw corsError;
      }

      // Provide specific error messages
      let errorMessage = 'Failed to upload file';
      
      if (error?.name === 'NoSuchBucket') {
        errorMessage = 'Bucket does not exist';
      } else if (error?.name === 'AccessDenied') {
        errorMessage = 'Access denied - check bucket permissions and IAM policy';
      } else if (error?.name === 'EntityTooLarge') {
        errorMessage = 'File size exceeds bucket limit';
      } else if (error?.name === 'NetworkingError') {
        errorMessage = 'Network error during upload';
      } else if (error?.message?.includes('timeout')) {
        errorMessage = error?.message;
      }

      throw new Error(`${errorMessage}: ${error?.message}`);
    } finally {
      // Always clean up
      this.activeUploads?.delete(uploadId);
    }
  }

  /**
   * Upload file with automatic retry on failure
   */
  async uploadFileWithRetry(file, options = {}) {
    const maxRetries = options?.maxRetries || 3;
    const retryDelay = options?.retryDelay || 2000; // Start with 2 seconds
    const uploadId = options?.uploadId || `upload-${Date.now()}-${Math.random()?.toString(36)?.substr(2, 9)}`;
    
    let lastError = null;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        this.log(`Upload attempt ${attempt}/${maxRetries}`, {
          uploadId,
          fileName: file?.name,
          attempt
        });
        
        const result = await this.uploadFile(file, {
          ...options,
          uploadId: `${uploadId}-attempt-${attempt}`
        });
        
        this.log(`Upload successful on attempt ${attempt}`, {
          uploadId,
          fileName: file?.name,
          attempt
        });
        
        return result;
        
      } catch (error) {
        lastError = error;
        
        // Don't retry on cancellation or CORS errors
        if (error?.name === 'UploadCanceled' || 
            error?.message === 'CORS_UPLOAD_ERROR' || 
            error?.troubleshooting?.type === 'CORS_UPLOAD') {
          this.log('Not retrying upload due to cancellation or CORS error', {
            uploadId,
            fileName: file?.name,
            errorType: error?.name || error?.message
          });
          throw error;
        }
        
        if (attempt < maxRetries) {
          // Exponential backoff
          const delay = retryDelay * Math.pow(2, attempt - 1);
          
          this.log(`Upload attempt ${attempt} failed, retrying in ${delay}ms...`, {
            uploadId,
            fileName: file?.name,
            attempt,
            error: error?.message,
            nextDelay: delay
          });
          
          await new Promise(resolve => setTimeout(resolve, delay));
        } else {
          this.logError(`Upload failed after ${maxRetries} attempts`, {
            uploadId,
            fileName: file?.name,
            attempts: maxRetries,
            lastError: error?.message
          });
        }
      }
    }
    
    // If we get here, all retries failed
    throw new Error(`Upload failed after ${maxRetries} attempts. Last error: ${lastError?.message}`);
  }

  /**
   * Generate a signed URL for downloading a file
   * @param {string} key - The S3 object key (file path)
   * @param {number} expiresIn - URL expiration time in seconds (default: 3600 = 1 hour)
   * @returns {Promise<string>} - Signed URL for downloading the file
   */
  async getSignedDownloadUrl(key, expiresIn = 3600) {
    try {
      this.log('Generating signed download URL...', {
        key: key,
        expiresIn: `${expiresIn}s`,
        bucket: this.bucketConfig?.bucketName
      });

      if (!this.s3Client || !this.bucketConfig) {
        throw new Error('S3 client not initialized. Call initializeClient first.');
      }

      const command = new GetObjectCommand({
        Bucket: this.bucketConfig?.bucketName,
        Key: key
      });

      // Generate signed URL with expiration
      const signedUrl = await getSignedUrl(this.s3Client, command, {
        expiresIn: expiresIn
      });

      this.log('Signed URL generated successfully', {
        key: key,
        expiresIn: `${expiresIn}s`,
        urlLength: signedUrl?.length
      });

      return signedUrl;

    } catch (error) {
      this.logError('Failed to generate signed URL', error);
      throw new Error(`Failed to generate download URL: ${error?.message}`);
    }
  }

  /**
   * Download a file from S3
   * @param {string} key - The S3 object key (file path)
   * @param {string} fileName - Optional filename for download (uses original if not provided)
   */
  async downloadFile(key, fileName = null) {
    try {
      this.log('Initiating file download...', {
        key: key,
        fileName: fileName || 'auto-detect',
        bucket: this.bucketConfig?.bucketName
      });

      if (!this.s3Client || !this.bucketConfig) {
        throw new Error('S3 client not initialized. Call initializeClient first.');
      }

      // Generate signed URL for download
      const signedUrl = await this.getSignedDownloadUrl(key, 300); // 5 minute expiration

      // Determine filename
      const downloadFileName = fileName || key?.split('/')?.pop();

      this.log('Fetching file as blob for download...', {
        key: key,
        fileName: downloadFileName
      });

      // Fetch the file as a blob to force download instead of navigation
      let response = await fetch(signedUrl);
      
      if (!response?.ok) {
        throw new Error(`Failed to fetch file: ${response?.status} ${response?.statusText}`);
      }

      const blob = await response?.blob();

      this.log('Creating blob URL for download...', {
        key: key,
        fileName: downloadFileName,
        blobSize: blob?.size
      });

      // Create blob URL
      const blobUrl = URL.createObjectURL(blob);

      // Create temporary anchor element to trigger download
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = downloadFileName;
      link.style.display = 'none';
      
      // Add to DOM, click, and remove
      document.body?.appendChild(link);
      link?.click();
      
      // Clean up
      setTimeout(() => {
        document.body?.removeChild(link);
        URL.revokeObjectURL(blobUrl); // Release blob URL
      }, 100);

      this.log('Download initiated successfully', {
        key: key,
        fileName: downloadFileName
      });

      return {
        success: true,
        key: key,
        fileName: downloadFileName,
        message: 'Download started'
      };

    } catch (error) {
      this.logError('File download failed', error);

      // Check for CORS errors
      if (error?.message?.includes('CORS') || 
          error?.message?.includes('Failed to fetch') ||
          error?.name === 'TypeError') {
        const corsError = new Error('CORS_DOWNLOAD_ERROR');
        corsError.originalError = error;
        corsError.troubleshooting = {
          type: 'CORS_DOWNLOAD',
          message: 'Your S3 bucket CORS policy does not allow file downloads from the browser',
          action: 'UPDATE_CORS_POLICY',
          details: {
            bucketName: this.bucketConfig?.bucketName,
            region: this.bucketConfig?.bucketRegion,
            errorType: error?.name,
            errorMessage: error?.message,
            requiredFix: 'Add GET method to your bucket CORS configuration'
          }
        };
        throw corsError;
      }

      throw new Error(`Failed to download file: ${error?.message}`);
    }
  }

  /**
   * Copy file URL to clipboard
   * @param {string} key - The S3 object key (file path)
   * @param {boolean} useSignedUrl - Whether to generate a signed URL (default: false, uses public URL)
   * @param {number} expiresIn - Signed URL expiration time in seconds (default: 3600)
   */
  async copyFileUrl(key, useSignedUrl = false, expiresIn = 3600) {
    try {
      this.log('Copying file URL to clipboard...', {
        key: key,
        useSignedUrl: useSignedUrl,
        expiresIn: useSignedUrl ? `${expiresIn}s` : 'N/A'
      });

      let fileUrl;

      if (useSignedUrl) {
        // Generate signed URL for temporary access
        fileUrl = await this.getSignedDownloadUrl(key, expiresIn);
        this.log('Generated signed URL for clipboard', {
          key: key,
          expiresIn: `${expiresIn}s`
        });
      } else {
        // Use public URL
        fileUrl = this.bucketConfig?.publicUrl 
          ? `${this.bucketConfig?.publicUrl}/${key}`
          : `https://${this.bucketConfig?.bucketName}.s3.${this.bucketConfig?.bucketRegion}.amazonaws.com/${key}`;
        
        this.log('Using public URL for clipboard', {
          key: key,
          url: fileUrl
        });
      }

      // Copy to clipboard using modern Clipboard API
      await navigator.clipboard?.writeText(fileUrl);

      this.log('URL copied to clipboard successfully', {
        key: key,
        urlType: useSignedUrl ? 'signed' : 'public'
      });

      return {
        success: true,
        key: key,
        url: fileUrl,
        urlType: useSignedUrl ? 'signed' : 'public',
        expiresIn: useSignedUrl ? expiresIn : null,
        message: 'URL copied to clipboard'
      };

    } catch (error) {
      this.logError('Failed to copy URL to clipboard', error);

      // Check for clipboard permission errors
      if (error?.name === 'NotAllowedError') {
        throw new Error('Clipboard access denied. Please allow clipboard permissions.');
      }

      throw new Error(`Failed to copy URL: ${error?.message}`);
    }
  }

  /**
   * Get shareable link for a file
   * @param {string} key - The S3 object key (file path)
   * @param {number} expiresIn - Link expiration time in seconds (default: 86400 = 24 hours)
   */
  async getShareableLink(key, expiresIn = 86400) {
    try {
      this.log('Generating shareable link...', {
        key: key,
        expiresIn: `${expiresIn}s (${expiresIn / 3600} hours)`,
        bucket: this.bucketConfig?.bucketName
      });

      // Generate signed URL with longer expiration for sharing
      const signedUrl = await this.getSignedDownloadUrl(key, expiresIn);

      this.log('Shareable link generated', {
        key: key,
        expiresIn: `${expiresIn}s`,
        expiresAt: new Date(Date.now() + expiresIn * 1000)?.toISOString()
      });

      return {
        success: true,
        key: key,
        shareUrl: signedUrl,
        expiresIn: expiresIn,
        expiresAt: new Date(Date.now() + expiresIn * 1000),
        message: `Link expires in ${expiresIn / 3600} hours`
      };

    } catch (error) {
      this.logError('Failed to generate shareable link', error);
      throw new Error(`Failed to generate shareable link: ${error?.message}`);
    }
  }

  /**
   * Get all active uploads
   */
  getActiveUploads() {
    return Array.from(this.activeUploads?.keys());
  }

  /**
   * Cancel all active uploads
   */
  cancelAllUploads() {
    const uploadIds = Array.from(this.activeUploads?.keys());
    this.log(`Canceling ${uploadIds?.length} active uploads...`);
    
    uploadIds?.forEach(uploadId => {
      this.cancelUpload(uploadId);
    });
    
    return uploadIds?.length;
  }

  /**
   * Upload multiple files with progress tracking
   */
  async uploadMultipleFiles(files, options = {}, progressCallback = null) {
    const results = {
      successful: [],
      failed: [],
      totalFiles: files?.length,
      completedFiles: 0
    };

    this.log(`Starting batch upload of ${files?.length} files...`);

    for (let i = 0; i < files?.length; i++) {
      const file = files?.[i];
      
      try {
        this.log(`Uploading file ${i + 1}/${files?.length}...`, {
          fileName: file?.name
        });

        // Call progress callback if provided
        if (progressCallback) {
          progressCallback({
            fileIndex: i,
            fileName: file?.name,
            progress: Math.round((i / files?.length) * 100),
            status: 'uploading'
          });
        }

        const result = await this.uploadFile(file, options);
        
        results?.successful?.push({
          file: file?.name,
          ...result
        });

        results.completedFiles++;

        // Call progress callback for completion
        if (progressCallback) {
          progressCallback({
            fileIndex: i,
            fileName: file?.name,
            progress: Math.round(((i + 1) / files?.length) * 100),
            status: 'completed',
            result: result
          });
        }

      } catch (error) {
        this.logError(`Failed to upload file ${file?.name}`, error);
        
        results?.failed?.push({
          file: file?.name,
          error: error?.message
        });

        results.completedFiles++;

        // Call progress callback for error
        if (progressCallback) {
          progressCallback({
            fileIndex: i,
            fileName: file?.name,
            progress: Math.round(((i + 1) / files?.length) * 100),
            status: 'error',
            error: error?.message
          });
        }
      }
    }

    this.log('Batch upload completed', {
      total: results?.totalFiles,
      successful: results?.successful?.length,
      failed: results?.failed?.length
    });

    return results;
  }

  /**
   * List files from S3 bucket
   * Now supports folder scoping
   */
  async listFiles(options = {}) {
    try {
      // CRITICAL FIX: Use explicit prefix if provided, otherwise use active folder
      // This ensures we properly scope listings to the active folder
      let prefix = options?.prefix || '';
      
      // If no prefix provided but active folder is set, use active folder as prefix
      if (!prefix && this.activeFolder) {
        prefix = this.activeFolder?.endsWith('/') 
          ? this.activeFolder 
          : `${this.activeFolder}/`;
      }

      this.log('Listing files from S3...', {
        explicitPrefix: options?.prefix,
        activeFolder: this.activeFolder,
        effectivePrefix: prefix,
        maxKeys: options?.maxKeys || 1000
      });

      if (!this.s3Client || !this.bucketConfig) {
        throw new Error('S3 client not initialized. Call initializeClient first.');
      }

      const command = new ListObjectsV2Command({
        Bucket: this.bucketConfig?.bucketName,
        Prefix: prefix,
        MaxKeys: options?.maxKeys || 1000,
        ContinuationToken: options?.continuationToken
      });

      this.log('Sending ListObjectsV2 command...', {
        bucket: this.bucketConfig?.bucketName,
        prefix: prefix
      });
      
      let response;
      try {
        response = await this.s3Client?.send(command);
      } catch (networkError) {
        // Enhanced CORS error detection
        this.logError('Network error during S3 request', networkError);
        
        // Check for various CORS error patterns
        const isCORSError = 
          // Direct CORS error
          networkError?.message?.includes('CORS') ||
          // Failed to fetch (browser blocking)
          networkError?.message?.includes('Failed to fetch') ||
          // Network request failed
          networkError?.message?.includes('NetworkingError') ||
          // TypeError from fetch API
          (networkError?.name === 'TypeError' && networkError?.message === 'Failed to fetch') ||
          // Fetch error (browser security)
          networkError?.name === 'FetchError' ||
          // Access control error
          networkError?.message?.includes('Access-Control-Allow-Origin');

        if (isCORSError) {
          const corsError = new Error('CORS_ERROR');
          corsError.originalError = networkError;
          corsError.troubleshooting = {
            type: 'CORS',
            message: 'Your S3 bucket needs CORS configuration to allow browser access',
            action: 'SHOW_CORS_GUIDE',
            details: {
              bucketName: this.bucketConfig?.bucketName,
              region: this.bucketConfig?.bucketRegion,
              errorType: networkError?.name,
              errorMessage: networkError?.message
            }
          };
          throw corsError;
        }
        
        // Re-throw if not a CORS error
        throw networkError;
      }

      // CRITICAL FIX: Add detailed response logging to diagnose empty file lists
      this.log('📦 Raw S3 Response Details', {
        keyCount: response?.KeyCount,
        contentsLength: response?.Contents?.length || 0,
        isTruncated: response?.IsTruncated,
        hasContents: !!response?.Contents,
        contentsIsArray: Array.isArray(response?.Contents),
        firstTwoKeys: response?.Contents?.slice(0, 2)?.map(item => item?.Key),
        activeFolder: this.activeFolder,
        statusCode: response?.$metadata?.httpStatusCode
      });

      this.log('List files successful', {
        keyCount: response?.KeyCount,
        isTruncated: response?.IsTruncated,
        activeFolder: this.activeFolder,
        statusCode: response?.$metadata?.httpStatusCode
      });

      // Format files for UI
      const files = response?.Contents?.map(item => {
        let fileUrl = this.bucketConfig?.publicUrl 
          ? `${this.bucketConfig?.publicUrl}/${item?.Key}`
          : `https://${this.bucketConfig?.bucketName}.s3.${this.bucketConfig?.bucketRegion}.amazonaws.com/${item?.Key}`;

        return {
          id: item?.ETag?.replace(/"/g, ''), // Use ETag as unique ID
          name: item?.Key?.split('/')?.pop(), // Get filename from key
          path: item?.Key,
          size: item?.Size,
          lastModified: item?.LastModified,
          type: this.getFileType(item?.Key),
          isFolder: item?.Key?.endsWith('/'),
          etag: item?.ETag,
          storageClass: item?.StorageClass,
          url: fileUrl
        };
      }) || [];

      // CRITICAL FIX: Add file result logging
      this.log('📂 File Listing Results', {
        totalFilesFound: files?.length,
        fileNames: files?.map(f => f?.name),
        activeFolder: this.activeFolder,
        prefix: prefix
      });

      return {
        success: true,
        files: files,
        isTruncated: response?.IsTruncated,
        nextContinuationToken: response?.NextContinuationToken,
        keyCount: response?.KeyCount,
        activeFolder: this.activeFolder
      };

    } catch (error) {
      this.logError('List files failed', error);

      // Check if it's a CORS error
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('CORS') ||
          error?.name === 'TypeError' && error?.message === 'Failed to fetch') {
        const corsError = new Error('CORS_ERROR');
        corsError.originalError = error;
        corsError.troubleshooting = {
          type: 'CORS',
          message: 'Your S3 bucket needs CORS configuration to allow browser access',
          action: 'SHOW_CORS_GUIDE'
        };
        throw corsError;
      }

      let errorMessage = 'Failed to list files from S3';
      
      if (error?.name === 'NoSuchBucket') {
        errorMessage = 'Bucket does not exist';
      } else if (error?.name === 'AccessDenied') {
        errorMessage = 'Access denied - check bucket permissions';
      } else if (error?.name === 'NetworkingError') {
        errorMessage = 'Network error - check your internet connection';
      }

      throw new Error(`${errorMessage}: ${error?.message}`);
    }
  }

  /**
   * Delete file from S3 bucket
   */
  async deleteFile(key) {
    try {
      this.log('Deleting file from S3...', {
        key: key,
        bucket: this.bucketConfig?.bucketName
      });

      if (!this.s3Client || !this.bucketConfig) {
        throw new Error('S3 client not initialized. Call initializeClient first.');
      }

      const command = new DeleteObjectCommand({
        Bucket: this.bucketConfig?.bucketName,
        Key: key
      });

      this.log('Sending DeleteObject command...');
      let response = await this.s3Client?.send(command);

      this.log('Delete successful', {
        key: key,
        statusCode: response?.$metadata?.httpStatusCode,
        requestId: response?.$metadata?.requestId
      });

      return {
        success: true,
        deletedKey: key,
        statusCode: response?.$metadata?.httpStatusCode
      };

    } catch (error) {
      this.logError('Delete file failed', error);

      let errorMessage = 'Failed to delete file from S3';
      
      if (error?.name === 'NoSuchKey') {
        errorMessage = 'File does not exist';
      } else if (error?.name === 'AccessDenied') {
        errorMessage = 'Access denied - check bucket permissions';
      } else if (error?.name === 'NetworkingError') {
        errorMessage = 'Network error during deletion';
      }

      throw new Error(`${errorMessage}: ${error?.message}`);
    }
  }

  /**
   * Delete multiple files from S3
   */
  async deleteMultipleFiles(keys, progressCallback = null) {
    const results = {
      successful: [],
      failed: [],
      totalFiles: keys?.length,
      completedFiles: 0
    };

    this.log(`Starting batch deletion of ${keys?.length} files...`);

    for (let i = 0; i < keys?.length; i++) {
      const key = keys?.[i];
      
      try {
        this.log(`Deleting file ${i + 1}/${keys?.length}...`, {
          key: key
        });

        if (progressCallback) {
          progressCallback({
            fileIndex: i,
            key: key,
            progress: Math.round((i / keys?.length) * 100),
            status: 'deleting'
          });
        }

        const result = await this.deleteFile(key);
        
        results?.successful?.push({
          key: key,
          ...result
        });

        results.completedFiles++;

        if (progressCallback) {
          progressCallback({
            fileIndex: i,
            key: key,
            progress: Math.round(((i + 1) / keys?.length) * 100),
            status: 'completed',
            result: result
          });
        }

      } catch (error) {
        this.logError(`Failed to delete file ${key}`, error);
        
        results?.failed?.push({
          key: key,
          error: error?.message
        });

        results.completedFiles++;

        if (progressCallback) {
          progressCallback({
            fileIndex: i,
            key: key,
            progress: Math.round(((i + 1) / keys?.length) * 100),
            status: 'error',
            error: error?.message
          });
        }
      }
    }

    this.log('Batch deletion completed', {
      total: results?.totalFiles,
      successful: results?.successful?.length,
      failed: results?.failed?.length
    });

    return results;
  }

  /**
   * Get file type from file name/extension
   */
  getFileType(fileName) {
    if (!fileName) return 'unknown';

    const extension = fileName?.split('.')?.pop()?.toLowerCase();
    
    const typeMap = {
      // Images
      jpg: 'image', jpeg: 'image', png: 'image', gif: 'image', 
      bmp: 'image', webp: 'image', svg: 'image', ico: 'image',
      
      // Videos
      mp4: 'video', avi: 'video', mov: 'video', wmv: 'video',
      flv: 'video', mkv: 'video', webm: 'video',
      
      // Documents
      pdf: 'document', doc: 'document', docx: 'document',
      txt: 'document', rtf: 'document', odt: 'document',
      
      // Spreadsheets
      xls: 'spreadsheet', xlsx: 'spreadsheet', csv: 'spreadsheet',
      
      // Archives
      zip: 'archive', rar: 'archive', tar: 'archive', 
      gz: 'archive', '7z': 'archive',
      
      // Audio
      mp3: 'audio', wav: 'audio', flac: 'audio', 
      aac: 'audio', ogg: 'audio',
      
      // Code
      js: 'code', jsx: 'code', ts: 'code', tsx: 'code',
      html: 'code', css: 'code', json: 'code', xml: 'code',
      py: 'code', java: 'code', cpp: 'code', c: 'code'
    };

    return typeMap?.[extension] || 'file';
  }

  /**
   * Get detailed error information
   */
  getErrorDetails(error) {
    return {
      name: error?.name,
      message: error?.message,
      code: error?.code || error?.$metadata?.httpStatusCode,
      statusCode: error?.$metadata?.httpStatusCode,
      requestId: error?.$metadata?.requestId,
      stack: error?.stack
    };
  }

  /**
   * Enable or disable debug mode
   */
  setDebugMode(enabled) {
    this.debugMode = enabled;
    this.log(`Debug mode ${enabled ? 'enabled' : 'disabled'}`);
  }
}

// Export singleton instance
export const s3UploadService = new S3UploadService();